import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Header from './Header';
import Pagination from './Pagination';
import {
  AlertCircle,
  Clock,
  Users,
  Eye,
  Download,
  Search,
  Filter,
  DollarSign,
  MapPin,
  Mail,
  Calendar,
  Send,
  FileText,
  TrendingDown,
  AlertTriangle,
  User,
  Phone,
  X
} from 'lucide-react';

const ModalViewPlotsCustomersLatePayers = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;
  let business_locationIDMain = localStorage.getItem('business_locationIDMain');
  
  let userCompanyID = localStorage.getItem('companyID');
  if(userCompanyID === 'Admin'){
    userCompanyID = 1;
  }
  userCompanyID = JSON.parse(userCompanyID);

  const [customersData, setCustomersData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    fetchDataForTablePlotsCustomers();
  }, []);

  useEffect(() => {
    if (searchValue.trim() === '') {
      setFilteredData(customersData);
    } else {
      const filtered = customersData.filter(customer =>
        Object.values(customer).some(value =>
          value?.toString().toLowerCase().includes(searchValue.toLowerCase())
        )
      );
      setFilteredData(filtered);
    }
    setCurrentPage(1);
  }, [searchValue, customersData]);

  const fetchDataForTablePlotsCustomers = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('apiType', 'fetchViewPlotsCustomersLatePayers');
    formData.append('companyID', userCompanyID);
    formData.append('business_locationIDMain', business_locationIDMain);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setCustomersData(response.data);
      setFilteredData(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error fetching data:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZM', { style: 'currency', currency: 'ZMW' }).format(amount);
  };

  const formatCurrencyTable = (amount) => {
    const number = typeof amount === 'number' ? amount : parseFloat(amount);
    return new Intl.NumberFormat('en-ZM', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2}).format(number);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF('landscape');
    const tableRows = [];

    filteredData.forEach((row, index) => {
      tableRows.push([
        index + 1,
        row.businessLocationName,
        row.plotNumber,
        row.plotNumberNew,
        row.name,
        row.dateContractSigned,
        row.lastDatePaid,
        formatCurrencyTable(row.plotPrice),
        formatCurrencyTable(row.plotPricesNew),
        formatCurrencyTable(row.monthlyInstallments),
        formatCurrencyTable(row.totalAmountPaid),
        formatCurrencyTable(row.balance)
      ]);
    });

    doc.autoTable({
      head: [['SN', 'Location', 'Plot #', 'New Plot #', 'Name', 'Contract Date', 'Last Paid', 'Old Price', 'New Price', 'Monthly', 'Total Paid', 'Balance']],
      body: tableRows,
      margin: { top: 20 },
      styles: {
        fontSize: 7,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [231, 76, 60],
        textColor: 255,
        fontStyle: 'bold'
      }
    });

    doc.save('late-payers-report.pdf');
  };

  const calculateTotals = (data) => {
    const totals = data.reduce((acc, customer) => {
      acc.totalAmountPaid += parseFloat(customer.totalAmountPaid) || 0;
      acc.totalBalance += parseFloat(customer.balance) || 0;
      acc.totalPlotValue += parseFloat(customer.plotPricesNew) || 0;
      acc.totalCustomers += 1;
      acc.totalMonthly += parseFloat(customer.monthlyInstallments) || 0;
      return acc;
    }, { 
      totalAmountPaid: 0, 
      totalBalance: 0, 
      totalPlotValue: 0,
      totalCustomers: 0,
      totalMonthly: 0
    });
    
    return totals;
  };

  const calculateDaysLate = (lastPaidDate) => {
    if (!lastPaidDate) return 'Unknown';
    
    const lastPaid = new Date(lastPaidDate);
    const today = new Date();
    const diffTime = Math.abs(today - lastPaid);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleViewStatement = (customer) => {
    setSelectedCustomer(customer);
    setShowStatementModal(true);
  };

  const handleSendEmail = (customer) => {
    setSelectedCustomer(customer);
    setEmailSubject(`Payment Reminder for ${customer.name} - Plot ${customer.plotNumber}`);
    setEmailMessage(`Dear ${customer.name},\n\nThis is a reminder that your payment is overdue for plot ${customer.plotNumber}.\n\nBalance Due: ${formatCurrency(customer.balance)}\nLast Payment Date: ${customer.lastDatePaid || 'Never'}\n\nPlease make your payment as soon as possible to avoid penalties.\n\nRegards,\nManagement Team`);
    setShowEmailModal(true);
  };

  const sendEmailCustomerStatementPDF = async () => {
    setSendingEmail(true);
    setEmailError('');
    setEmailSuccess(false);

    try {
      const formData = new FormData();
      formData.append('apiType', 'sendEmailCustomerStatementPDF');
      formData.append('email', selectedCustomer.customersEmail);
      formData.append('subject', emailSubject);
      formData.append('message', emailMessage);

      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSendingEmail(false);
      if (response.data.includes('success')) {
        setEmailSuccess(true);
        setTimeout(() => {
          setShowEmailModal(false);
          setEmailSuccess(false);
        }, 2000);
      } else {
        setEmailError(response.data);
      }
    } catch (error) {
      setSendingEmail(false);
      setEmailError('Failed to send email. Please try again.');
    }
  };

  const sendEmailPlotsLoginCredentials = async (customerId) => {
    try {
      const formData = new FormData();
      formData.append('apiType', 'sendEmailPlotsLoginCredentials');
      formData.append('id', customerId);

      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      alert(response.data);
    } catch (error) {
      alert('Failed to send login credentials. Please try again.');
    }
  };

  const confirmForPaymentReminders = async (customerId) => {
    try {
      const formData = new FormData();
      formData.append('apiType', 'comfirmClientAvailability');
      formData.append('customerID', customerId);
      formData.append('customerType', 'Plots');

      const response = await axios.post(`${server_app_api_php}/server/payment_remindersEmailList.php`, formData);
      alert('Confirmed for automated payment reminders');
    } catch (error) {
      alert('Failed to confirm for payment reminders.');
    }
  };

  // Calculate pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const totals = calculateTotals(customersData);
  const filteredTotals = calculateTotals(filteredData);

  return (
    <>
      <Header />
      <main className="lg:ml-64 pt-16 min-h-screen bg-gray-50">
        <div className="p-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl">
                    <AlertCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Late Payers</h1>
                    <p className="text-gray-600">Customers with overdue payments</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleExportPDF}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl hover:opacity-90 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export PDF</span>
                  </button>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Late Payers</p>
                      <p className="text-2xl font-bold text-gray-900">{totals.totalCustomers}</p>
                      <div className="flex items-center space-x-1 mt-1">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <span className="text-xs text-red-600">Overdue payments</span>
                      </div>
                    </div>
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Outstanding Balance</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.totalBalance)}</p>
                    </div>
                    <TrendingDown className="w-8 h-8 text-orange-500" />
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Amount Paid</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.totalAmountPaid)}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatCurrency(totals.totalPlotValue)} total plot value
                      </p>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-500" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Search late payers..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className="pl-10 pr-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white w-64"
                      />
                    </div>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                      className="px-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="5">5 entries</option>
                      <option value="10">10 entries</option>
                      <option value="25">25 entries</option>
                      <option value="50">50 entries</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-1 text-red-600">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        {filteredData.length} late payers
                        {searchValue && ` (filtered)`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                {loading ? (
                  <div className="p-12 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    <p className="mt-4 text-gray-500">Loading late payers data...</p>
                  </div>
                ) : (
                  <>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-red-500 to-orange-600">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">SN</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            <MapPin className="w-4 h-4 inline mr-1" />
                            Location
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Plot #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            <Clock className="w-4 h-4 inline mr-1" />
                            Last Paid
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Days Late</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            
                            Plot Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Monthly</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Total Paid</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Balance</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {currentRows.map((customer, index) => {
                          const daysLate = calculateDaysLate(customer.lastDatePaid);
                          return (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{indexOfFirstRow + index + 1}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="flex items-center">
                                  <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                                  {customer.businessLocationName}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-blue-600">{customer.plotNumber}</div>
                                  <div className="text-xs text-gray-500">New: {customer.plotNumberNew}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                                  <div className="text-xs text-gray-500">{customer.customersEmail}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="flex items-center">
                                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                  {customer.lastDatePaid || 'Never'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  daysLate > 30 ? 'bg-red-100 text-red-800' : 
                                  daysLate > 15 ? 'bg-orange-100 text-orange-800' : 
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {daysLate} days
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {formatCurrency(customer.plotPricesNew)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(customer.monthlyInstallments)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                                {formatCurrency(customer.totalAmountPaid)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                  {formatCurrency(customer.balance)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleViewStatement(customer)}
                                    className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                  >
                                    <Eye className="w-4 h-4" />
                                    <span>View</span>
                                  </button>
                                  <button
                                    onClick={() => handleSendEmail(customer)}
                                    className="flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                                  >
                                    <Send className="w-4 h-4" />
                                    <span>Remind</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan="7" className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                            Filtered Totals:
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {formatCurrency(filteredTotals.totalMonthly)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {formatCurrency(filteredTotals.totalAmountPaid)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-600">
                            {formatCurrency(filteredTotals.totalBalance)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>

                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleRowsPerPageChange}
                      totalItems={filteredData.length}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Statement Modal */}
        {showStatementModal && selectedCustomer && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-red-500 to-orange-600 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Customer Statement - Late Payer</h3>
                <button
                  onClick={() => setShowStatementModal(false)}
                  className="text-white hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="px-6 py-6">
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-gray-500">Customer Name</p>
                      <p className="font-medium text-gray-900">{selectedCustomer.name}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-gray-500">Email Address</p>
                      <p className="font-medium text-gray-900">{selectedCustomer.customersEmail}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-gray-500">Phone Number</p>
                      <p className="font-medium text-gray-900">{selectedCustomer.customersPhone}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-gray-500">Plot Number</p>
                      <p className="font-medium text-gray-900">{selectedCustomer.plotNumber}</p>
                    </div>
                  </div>
                </div>
                
                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl">
                  <div className="flex items-center space-x-3">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                    <div>
                      <h4 className="text-lg font-medium text-red-800">Payment Overdue</h4>
                      <p className="text-red-600">
                        Last payment: {selectedCustomer.lastDatePaid || 'Never'} • 
                        Overdue by: {calculateDaysLate(selectedCustomer.lastDatePaid)} days
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Financial Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-green-50 rounded-xl">
                      <p className="text-sm text-green-600">Plot Price</p>
                      <p className="text-2xl font-bold text-green-700">{formatCurrency(selectedCustomer.plotPricesNew)}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-xl">
                      <p className="text-sm text-blue-600">Total Paid</p>
                      <p className="text-2xl font-bold text-blue-700">{formatCurrency(selectedCustomer.totalAmountPaid)}</p>
                    </div>
                    <div className="p-4 bg-red-100 rounded-xl">
                      <p className="text-sm text-red-800">Overdue Balance</p>
                      <p className="text-2xl font-bold text-red-900">{formatCurrency(selectedCustomer.balance)}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowStatementModal(false);
                      handleSendEmail(selectedCustomer);
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-600 text-white rounded-lg hover:opacity-90 transition-all"
                  >
                    Send Reminder
                  </button>
                  <button
                    onClick={() => setShowStatementModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Modal */}
        {showEmailModal && selectedCustomer && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg">
              <div className="bg-gradient-to-r from-red-500 to-orange-600 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Send Payment Reminder</h3>
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="text-white hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="px-6 py-6">
                {emailSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="w-5 h-5 text-green-600" />
                      <span className="text-green-800">Reminder sent successfully!</span>
                    </div>
                  </div>
                )}
                
                {emailError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <span className="text-red-800">{emailError}</span>
                    </div>
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      To
                    </label>
                    <input
                      type="email"
                      value={selectedCustomer.customersEmail}
                      readOnly
                      className="w-full px-4 py-2 bg-gray-100 rounded-lg border border-gray-300"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message
                    </label>
                    <textarea
                      value={emailMessage}
                      onChange={(e) => setEmailMessage(e.target.value)}
                      rows="6"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowEmailModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendEmailCustomerStatementPDF}
                    disabled={sendingEmail}
                    className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {sendingEmail ? 'Sending...' : 'Send Reminder'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default ModalViewPlotsCustomersLatePayers;